import React, { memo, useMemo, useCallback } from 'react';
import { observer } from 'mobx-react-lite';
import { useHistory } from 'react-router-dom';
import { Clock, Heart, MapPin } from 'lucide-react';

import { IActivity, PlayPurpose } from '../../interface/Activity';
import {
  formatActivityPrice,
  hasDisplayablePrice,
  isPriceTba,
} from '../../helpers/activityPricing';
import { Avatar, AvatarImage, AvatarFallback } from '../ui/avatar';
import { EmptyAvatar } from '../ui/empty-avatar';
import { getRating } from '../../helpers/user';
import { Card, CardContent } from '../ui/card';
import { DateTime } from 'luxon';
import { userStore } from '../../stores/UserStore';
import { activityStore } from '../../stores/ActivityStore';
import { Spinner } from '../ui/spinner';
import { Icon } from '../Primitives';
import {
  getAcceptedOrReservedPlayers,
  getGenderSlotDescriptors,
  isGenderManaged,
} from '../../helpers/activityGenderSlots';
import { getGenderColor, getGenderTint } from '../../helpers/genderDisplay';
import {
  ACTIVITY_TAG_COLORS,
  getActivityPurposeTag,
  getActivityPurposeTagStyle,
  getActivityTagOutlineStyle,
} from '../../helpers/activityDisplay';
import ActivityStatusPill from './ActivityStatusPill';

type Props = {
  activity: IActivity;
  fixedWidth?: boolean;
  showFavouriteButton?: boolean;
  purposeOverride?: PlayPurpose;
};

const CompactActivityCard: React.FC<Props> = observer(({
  activity,
  fixedWidth = false,
  showFavouriteButton = false,
  purposeOverride,
}) => {
  const history = useHistory();
  
  // Memoize click handler
  const handleClick = useCallback(() => {
    history.push(`/plays/${activity.id}`);
  }, [history, activity.id]);

  const {
    sportInfo,
    sport,
    name,
    maxPlayer,
    start,
    end,
    timezone,
    locations,
    minRating,
    maxRating,
    players,
    courtBooked,
    isRanked,
    waitlisted,
    requestToJoin,
    createdById,
    status,
  } = activity;

  const { account: { profileId, profile: currentUserProfile } } = userStore;
  const isFavourited =
    activityStore.isActivityFavourited(activity.id) || !!activity.favourite;
  const isFavouriteLoading = activityStore.isActivityFavouriteLoading(
    activity.id
  );
  const isOnWaitlist = waitlisted?.includes(profileId);
  const isHosting = createdById === profileId;
  const currentPlayerStatus = players?.find((p) => p.profileId === profileId)?.status;
  const isInvited = currentPlayerStatus === 'INVITED' && createdById !== profileId && status === 'PLANNING';
  const purposeTag = getActivityPurposeTag(activity, purposeOverride);
  const hostingTagStyle = getActivityTagOutlineStyle(ACTIVITY_TAG_COLORS.HOSTING);
  const rankedTagStyle = getActivityTagOutlineStyle(ACTIVITY_TAG_COLORS.RANKED);
  const invitedTagStyle = getActivityTagOutlineStyle(ACTIVITY_TAG_COLORS.INVITED);
  const waitlistTagStyle = getActivityTagOutlineStyle(ACTIVITY_TAG_COLORS.WAITLIST);

  const dtStart = DateTime.fromISO(start).setZone(timezone || 'local');
  const dtEnd = DateTime.fromISO(end).setZone(timezone || 'local');
  const today = DateTime.local();
  const isDifferentYear = dtStart.year !== today.year;
  const dayLabel = dtStart.hasSame(today, 'day')
    ? 'Today'
    : dtStart.hasSame(today.plus({ days: 1 }), 'day')
    ? 'Tomorrow'
    : dtStart.toFormat(isDifferentYear ? 'ccc d LLL yyyy' : 'ccc d LLL');

  // Format time: show "9 am" if on the hour, "9:30 am" if not
  const formatTime = (dt: DateTime) => {
    const format = dt.minute === 0 ? 'h a' : 'h:mm a';
    return dt.toFormat(format).toLowerCase();
  };
  const timeRange = `${formatTime(dtStart)} - ${formatTime(dtEnd)}`;
  const activityLocation = locations[0];

  const hasPrice = hasDisplayablePrice(activity);
  const priceAmount = hasPrice ? formatActivityPrice(activity) : '';
  const showPricePerPerson = hasPrice && !isPriceTba(activity);

  // Level text with 1dp
  const MIN = 1;
  const MAX = 7;
  const fmt = (n?: number) =>
    typeof n === 'number' ? n.toFixed(1) : '';
  const isRestricted =
    requestToJoin &&
    typeof minRating === 'number' &&
    typeof maxRating === 'number' &&
    (minRating !== MIN || maxRating !== MAX);
  const levelText = isRestricted
    ? `${
        minRating === maxRating
          ? fmt(minRating)
          : `${fmt(minRating)} - ${fmt(maxRating)}`
      }`
    : 'All level';

  // Memoize going players: accepted + reserved/placeholder contacts
  const acceptedPlayers = useMemo(() => getAcceptedOrReservedPlayers(activity), [activity]);
  const slotDescriptors = useMemo(() => {
    if (isGenderManaged(activity)) return getGenderSlotDescriptors(activity);
    return Array.from({ length: Math.max(maxPlayer - acceptedPlayers.length, 0) }, (_, index) => ({
      key: `open-${index}`,
      slotType: 'open' as const,
    }));
  }, [activity, acceptedPlayers.length, maxPlayer]);

  const handleFavouriteClick = useCallback(
    (event: React.MouseEvent<HTMLButtonElement>) => {
      event.stopPropagation();
      activityStore.toggleActivityFavourite(activity);
    },
    [activity]
  );

  return (
    <Card
      className={`flex-shrink-0 ${fixedWidth ? 'w-[360px]' : 'w-full'} cursor-pointer hover:shadow-lg transition-shadow`}
      onClick={handleClick}
    >
      <CardContent className="h-full flex flex-col !p-4">
        {/* Top row: sport + status/favourite actions */}
        <div className="flex items-center min-h-[24px]">
          <div className="flex items-center gap-1 text-xs text-muted-foreground">
            <span className="capitalize">{sportInfo?.name?.toLowerCase()}</span>
            <img
              className="h-3 w-3"
              src={`https://rovo.fit/assets/sports/sport-${sportInfo.uri}-icon.png`}
              alt={sportInfo?.name || 'sport'}
              loading="lazy"
              decoding="async"
            />
          </div>
          <div className="ml-auto flex items-center gap-2">
            <ActivityStatusPill activity={activity} className="shrink-0" />
            {showFavouriteButton && (
              <button
                type="button"
                onClick={handleFavouriteClick}
                aria-label={
                  isFavourited
                    ? 'Remove from favourite activities'
                    : 'Add to favourite activities'
                }
                aria-pressed={isFavourited}
                className={`inline-flex h-8 w-8 items-center justify-center rounded-full transition-colors hover:bg-[#E5402E]/10 ${
                  isFavourited ? 'text-[#E5402E]' : 'text-foreground'
                }`}
              >
                {isFavouriteLoading ? (
                  <Spinner
                    size={16}
                    className={isFavourited ? 'text-[#E5402E]' : 'text-foreground'}
                  />
                ) : (
                  <Heart
                    className="h-5 w-5"
                    fill={isFavourited ? 'currentColor' : 'none'}
                  />
                )}
              </button>
            )}
          </div>
        </div>

        {/* Title - always 1 line */}
        <div className="line-clamp-1 text-base font-semibold">{name}</div>

        {/* Avatars */}
        <div
          className="mt-2 overflow-x-auto overflow-y-hidden scrollbar-hover -mx-4"
          style={{ paddingBottom: '4px' }}
        >
          <div className="flex items-center gap-4 px-4">
            {acceptedPlayers.map((p) => {
              // Use current user's profile for their own avatar to ensure it's up-to-date
              const isCurrentUser = p.profileId === profileId;
              const playerProfile = isCurrentUser ? currentUserProfile : p.profile;
              // Get rating from sportProfile or find from sportProfiles by activity's sport
              const effectiveSportProfile = playerProfile?.sportProfile || playerProfile?.sportProfiles?.find(sp => sp.sport === sport);
              const rating = getRating(effectiveSportProfile);
              return (
                <Avatar
                  key={p.profileId}
                  className="h-10 w-10"
                  pulseLevel={playerProfile?.pulseLevel}
                  rating={rating}
                >
                  <AvatarImage src={playerProfile?.picture} alt="player" />
                  <AvatarFallback>?</AvatarFallback>
                </Avatar>
              );
            })}
            {/* Empty placeholders */}
            {slotDescriptors.slice(0, 2).map((slot) => {
              if (slot.slotType === 'open') {
                return (
                  <EmptyAvatar key={slot.key} className="h-10 w-10 flex-shrink-0" />
                );
              }

              const genderColor = getGenderColor(slot.slotType);

              return (
                <div
                  key={slot.key}
                  className="h-10 w-10 flex-shrink-0 rounded-full border border-dashed flex items-center justify-center"
                  style={{
                    backgroundColor: getGenderTint(slot.slotType),
                    borderColor: `${genderColor}66`,
                  }}
                >
                  <Icon
                    icon={slot.slotType}
                    size="sm"
                    stroke={slot.slotType === 'female' ? 'pinkEnergetic' : 'preservingBlue'}
                  />
                </div>
              );
            })}
          </div>
        </div>

        {/* Player count */}
        <div className="mt-2 text-xs text-foreground">
          {acceptedPlayers.length}/{maxPlayer} playing
          {isRestricted && <> · <span className="font-semibold">Level {levelText}</span></>}
          {hasPrice && <> · <span className="font-semibold">{priceAmount}</span>{showPricePerPerson && <span className="text-muted-foreground"> /person</span>}</>}
        </div>

        {/* Tags - horizontally scrollable */}
        <div
          className="mt-2 min-h-[24px] overflow-x-auto [&::-webkit-scrollbar]:hidden -mx-4"
          style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
        >
          <div className="flex items-center gap-2 px-4">
            {isHosting && (
              <div className="inline-flex flex-shrink-0 items-center justify-center rounded-md px-2 py-1 text-xs font-semibold uppercase" style={hostingTagStyle}>
                HOSTING
              </div>
            )}
            {isRanked && (
              <div className="inline-flex flex-shrink-0 items-center justify-center rounded-md px-2 py-1 text-xs font-semibold uppercase" style={rankedTagStyle}>
                RANKED GAME
              </div>
            )}
            {purposeTag && (
              <div className="inline-flex flex-shrink-0 items-center justify-center rounded-md px-2 py-1 text-xs font-semibold uppercase" style={getActivityPurposeTagStyle(purposeTag.purpose)}>
                {purposeTag.label}
              </div>
            )}
            {isInvited && (
              <div className="inline-flex flex-shrink-0 items-center justify-center rounded-md px-2 py-1 text-xs font-semibold uppercase" style={invitedTagStyle}>
                YOU'RE INVITED
              </div>
            )}
            {isOnWaitlist && (
              <div className="inline-flex flex-shrink-0 items-center justify-center rounded-md px-2 py-1 text-xs font-semibold uppercase" style={waitlistTagStyle}>
                ON WAITLIST
              </div>
            )}
          </div>
        </div>

        {/* Divider */}
        <div className="my-3 h-px bg-border" />

        {/* Time */}
        <div className="flex items-start gap-2 text-sm">
          <Clock className="mt-0.5 h-4 w-4 text-muted-foreground flex-shrink-0" />
          <div className="min-w-0">
            <div className="truncate">
              {dayLabel}, {timeRange}
            </div>
            <div className="text-xs text-muted-foreground">One-time activity</div>
          </div>
        </div>

        {/* Location */}
        <div className="mt-2 flex items-center gap-2 text-sm">
          <MapPin className="h-4 w-4 text-muted-foreground flex-shrink-0" />
          <div className="flex items-center gap-x-1 text-sm truncate min-w-0">
            {courtBooked && <span className="flex-shrink-0">Court booked</span>}
            {courtBooked && <span className="flex-shrink-0">·</span>}
            <span className="truncate">
              {activityLocation?.venueDetail?.venueName || activityLocation?.name}
            </span>
          </div>
        </div>
      </CardContent>
    </Card>
  );
});

export default memo(CompactActivityCard);
